import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import config from '../Config'
import { HTML_LANG } from '../i18n'
import { KEYS, readJSON, writeJSON } from './storage'

/* Product analytics (analytics spec): PostHog only. It follows the cookie
   banner (spec 3.6): before a choice nothing is sent or stored (events wait
   in memory), "Prihvati sve" tracks fully with the install id as
   distinct_id, "Samo neophodni" tracks without cookies or localStorage
   (PostHog's cookieless mode). The choice is remembered. */

const SESSION_GAP_MS = 30 * 60 * 1000
const QUEUE_MAX = 50
const PRODUCTION_HOSTS = ['instapres.com', 'www.instapres.com']

let posthog = null // loaded after the visitor's choice (its own chunk)
let loading = false
let started = false
let queue = []
let consented = false

/* 'all' | 'necessary' | null (not asked yet). */
export function readConsent() {
  const value = readJSON(KEYS.consent, null)
  return value === 'all' || value === 'necessary' ? value : null
}

const env = () =>
  config.IS_DEV ? 'dev' : PRODUCTION_HOSTS.includes(window.location.hostname) ? 'prod' : 'test'

// No undefined values; null means "doesn't apply".
const clean = (props) => {
  if (!props) return undefined
  const out = {}
  Object.keys(props).forEach((k) => {
    if (props[k] !== undefined) out[k] = props[k]
  })
  return out
}

const run = (fn) => {
  if (started) {
    try {
      fn()
    } catch (e) {
      // analytics never breaks the page
    }
  } else if (queue.length < QUEUE_MAX) queue.push(fn)
}

/* session_number and days_since_install (spec 3.4): only with consent,
   since they live in localStorage. */
function sessionProps() {
  const now = Date.now()
  const s = readJSON(KEYS.analyticsSession, null) || { n: 0, lastAt: 0, firstAt: now }
  if (now - s.lastAt > SESSION_GAP_MS) s.n += 1
  s.lastAt = now
  writeJSON(KEYS.analyticsSession, s)
  const day = (t) => new Date(t).setHours(0, 0, 0, 0)
  return {
    session_number: s.n,
    days_since_install: Math.round((day(now) - day(s.firstAt)) / 86400000),
  }
}

export function track(name, props) {
  if (config.IS_DEV) console.debug('[analytics]', name, props || '')
  run(() => {
    if (consented) posthog.register(sessionProps())
    posthog.capture(name, clean(props))
  })
}

// Super properties: on every event from now on.
export const registerProps = (props) => run(() => posthog.register(clean(props)))

// Person properties (spec 6); there are no persons without consent.
export const setPersonProps = (props) =>
  run(() => consented && posthog.setPersonProperties(clean(props)))

/* Once the visitor has chosen (now or on an earlier visit). installId: the
   ads rollout id, only with consent (Utils/ads.js makes it). */
export async function startAnalytics(consent, installId) {
  if (loading) return
  loading = true
  consented = consent === 'all'
  try {
    posthog = (await import('posthog-js')).default
  } catch (e) {
    return // blocked or offline: no analytics this visit
  }
  posthog.init(config.POSTHOG_KEY, {
    api_host: config.POSTHOG_HOST,
    ui_host: config.POSTHOG_UI_HOST,
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: !consented,
    capture_pageview: 'history_change', // the SPA's route changes too
    capture_pageleave: false,
    autocapture: false,
    capture_exceptions: true,
    disable_session_recording: true,
    person_profiles: consented ? 'always' : 'identified_only',
    ...(consented && installId ? { bootstrap: { distinctID: installId } } : {}),
  })
  if (consented) posthog.opt_in_capturing({ captureEventName: false })
  else posthog.opt_out_capturing()
  posthog.register({
    env: env(),
    lang_variant: HTML_LANG,
    ...(consented ? sessionProps() : { ads_bucket: 'off' }),
  })
  started = true
  const waiting = queue
  queue = []
  waiting.forEach(run)
}

// Where something happened (spec 5), from the page's address.
export function surfaceOf(location) {
  const path = location.pathname
  if (path === '/') return 'home'
  if (path.startsWith('/kategorija/')) return 'category'
  if (path.startsWith('/tema/')) return 'topic'
  if (path.startsWith('/izvor/')) return 'source'
  if (path === '/moje-teme') return 'my_topics'
  if (path === '/pretraga') return 'search_results'
  if (path === '/istrazi') return 'explore'
  // The first page of a visit is a shared link (Pages/Story.js).
  if (path.startsWith('/prica/')) return location.key === 'default' ? 'shared_link' : 'story'
  if (path.startsWith('/krizaljka')) return 'crossword'
  return null
}

export const useSurface = () => surfaceOf(useLocation())

// How a page was reached, from the link's state (none: an outside link).
export const entryOf = (location) => location.state?.entry || 'deep_link'

/* topic_open, source_open, story_open…: once a page shows something new
   (props change), with how it was reached. */
export function useOpenEvent(name, props, ready = true) {
  const location = useLocation()
  const key = JSON.stringify(props)
  useEffect(() => {
    if (ready) track(name, { ...JSON.parse(key), entry: entryOf(location) })
    // location.state only matters at the moment the page opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, key, ready])
}
